import {Component, DestroyRef, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {TagsDetailFacade} from "../../services";
import {TagDto} from "../../domain";
import {InvalidArgumentException, TagColor, TagColors, TagType} from "../../../../domain";
import {catchError, filter, mergeMap, of, tap} from "rxjs";
import {FormControl, Validators} from "@angular/forms";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'app-tag-detail',
  templateUrl: './tag-detail.component.html',
  styleUrls: ['./tag-detail.component.scss']
})
export class TagDetailComponent implements OnInit {
  @Output() saved = new EventEmitter<TagDto>()
  @Output() notFound = new EventEmitter<void>()
  @Output() saveFail = new EventEmitter<string>()
  @Input() loading = false
  @Output() loadingChange = new EventEmitter<boolean>()
  protected colors = TagColors
  protected tagNameForm = new FormControl<string>('', Validators.required)
  protected tag: TagDto = {
    type: TagType.CUSTOM,
    name: '',
    color: TagColors[0],
    tagId: 0,
    companies: []
  }
  protected tokens = {
    tagName: 'TAGS.TAG_NAME',
    requiredField: 'FORMS.REQUIRED_FIELD',
    color: 'TAGS.COLOR',
  }
  protected errors = {
    name: '',
    color: ''
  }

  constructor(private tagsFacade: TagsDetailFacade, private destroyRef: DestroyRef) {
  }

  @Input() set companies(value: string[]) {
    this.tag.companies = value || []
  }

  ngOnInit() {
    this.loadingChange.emit(false)
    this.loading = false
    this.tagsFacade.currentTag$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(id => id !== 0),
        tap(() => {
          this.loading = true
          this.loadingChange.emit(true)
        }),
        mergeMap(tagId => this.tagsFacade.findTagById(tagId)),
        catchError(() => of(null)))
      .subscribe({
        next: tag => {
          if (tag) {
            this.tag = tag
            this.tagNameForm.setValue(tag.name)
          } else {
            this.notFound.emit()
          }
          this.loading = false
          this.loadingChange.emit(false)
        }
      })
    this.listenToInputChange()
    this.listenToSave()
  }

  selectedColor(color: TagColor) {
    this.tag.color = color
    this.errors['color'] = ''
  }

  private listenToInputChange() {
    this.tagNameForm.valueChanges
      .subscribe(value => {
        this.tag.name = value || ''
        this.errors.name = value ? '' : this.tokens.requiredField
      })
  }

  private listenToSave() {
    this.tagsFacade.saveConfirm$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.saveTag())
  }

  private saveTag() {
    try {
      const validatedTag = this.tagsFacade.validateTag({...this.tag})
      const saveTask = validatedTag.tagId
        ? this.tagsFacade.editTag(validatedTag)
        : this.tagsFacade.createTag(validatedTag)

      saveTask.pipe(catchError((e) => {
        this.handleError(e)
        return of(null)
      })).subscribe((tag) => {
        if (tag) this.saved.emit(tag)
      })
    } catch (e) {
      this.handleError(e as Error)
    }
  }

  private handleError = (error: Error) => {
    if (error instanceof InvalidArgumentException) {
      const {field, reason} = error.getErrorDetails()

      if (field === 'name' || field === 'color')
        this.errors[field] = reason === 'required' ? this.tokens.requiredField : ''
      this.saveFail.emit()
    } else this.saveFail.emit('TAGS.ERROR_SAVING_TAG')
  }

}
